import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import { SessionContext } from '@/shared/session';

import type { PermissionCode, PermissionContextValue } from './permissionTypes';

/**
 * Authorization / permission context.
 *
 * Provides `hasPermission(code)` checks for role-based UI visibility. The granted
 * codes come from the active session (`GET /api/v1/me` → tenant-scoped permissions,
 * mapped by the BFF from Keycloak roles). They can be overridden via the
 * `permissions` prop (tests / Storybook); with neither a prop nor a session, nothing
 * is granted — see {@link DEFAULT_PERMISSIONS}.
 */

/**
 * What is granted when there is no session and no explicit override: **nothing**.
 *
 * <p>This used to be a near-super-user list (`settings.*`, `employees.manage`, `roles.manage`,
 * `catalog.manage`, …) so that an isolated render still showed a populated UI. It was not reachable
 * inside the app — `SessionContext` always supplies a `string[]`, so `??` never fell through to it —
 * but it was a landmine: one provider-order change and the portal would grant near-super-user access
 * with no error and nothing to notice. The portal's blast radius includes credentials management and
 * staff removal, so the fallback fails closed (GAP F-8, plan-auth-001 Phase 3.9; `platform-crm-app`
 * made the same call in its `adr-001`).
 *
 * <p>Offline dev is unaffected: `VITE_AUTH_MODE=mock` renders `MockSessionProvider`, whose `MOCK_ME`
 * carries the permissive list. Tests that need permissions pass them via the `permissions` prop.
 */
const DEFAULT_PERMISSIONS: PermissionCode[] = [];

/** Match a granted code against a requested code, honouring wildcards. */
function matchesPermission(granted: PermissionCode, requested: PermissionCode): boolean {
  if (granted === '*') {
    return true;
  }
  if (granted.endsWith('.*')) {
    const prefix = granted.slice(0, -1); // keep trailing dot, e.g. "settings."
    return requested.startsWith(prefix);
  }
  return granted === requested;
}

// Context + provider intentionally co-located (see required project structure);
// the hook lives in usePermissions.ts.
// eslint-disable-next-line react-refresh/only-export-components
export const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
  /**
   * Override the granted permissions (tests / Storybook).
   *
   * Omitting it outside a `SessionProvider` grants nothing — a test that needs a permission must say
   * which one, which is also what makes the test document the access it is exercising.
   */
  permissions?: PermissionCode[];
}

export function PermissionProvider({ children, permissions }: PermissionProviderProps) {
  const session = useContext(SessionContext);
  const granted = permissions ?? session?.permissions ?? DEFAULT_PERMISSIONS;

  const value = useMemo<PermissionContextValue>(() => {
    const hasPermission = (permission: PermissionCode): boolean =>
      granted.some((code) => matchesPermission(code, permission));

    return {
      permissions: granted,
      hasPermission,
      hasAnyPermission: (requested) => requested.some(hasPermission),
      hasAllPermissions: (requested) => requested.every(hasPermission),
    };
  }, [granted]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}
